/**
 * Елементи інтерфейсу Collabora, які ховаємо CSS-ом у документі `iframe`
 * (`SPEC-012` §7.10.1, P-38; легасі `FileInput.jsx:340-352`).
 *
 * Варіант B (замовник, 2026-10-08, Q12.8): редактор живе на домені тенанта,
 * тобто в origin застосунку, тож документ `iframe` нам доступний — як у легасі.
 *
 * ⚠️ Селектори прив'язані до розмітки версії Collabora. Звірено наживо під
 * `COLLABORA_VERSION` нижче (= `.docker/.env`, образ `collabora/code`). Нова
 * версія образу — звірити заново (акт S7): селектор без відповідника замінити
 * відповідником тієї версії, а що не має відповідника — показати замовнику.
 */
export const COLLABORA_VERSION = '25.04.9.4'

export interface HiddenCollaboraElement {
    selector: string
    /** Що це для людини — для акту S7 */
    what: string
    /** Селектор легасі, якщо в цій версії він інший */
    legacy?: string
}

export const COLLABORA_HIDDEN_ELEMENTS: readonly HiddenCollaboraElement[] = [
    // Обгортка-модалка з `iframe` welcome.html («Ознайомтеся з новою версією…»)
    { selector: '.iframe-welcome-wrap', what: 'вікно привітання' },
    // `#renamedocument` у 25.04 немає: назву показує й перейменовує поле
    // `#document-name-input` усередині `#document-titlebar`
    { selector: '#document-titlebar', what: 'поле перейменування (назва документа)', legacy: '#renamedocument' },
    // Кнопка на вкладці «Вигляд» і плаваюча кнопка `#navigator-floating-icon` —
    // обидві мають ці три класи
    { selector: '.unotoolbutton.notebookbar.unoNavigator', what: 'кнопка «Навігатор»' },
    // `#view-navigator` у 25.04 немає (кнопка тепер `view-navigator<N>`):
    // панель навігатора — бічна `nav#navigation-sidebar`
    { selector: '#navigation-sidebar', what: 'панель навігатора', legacy: '#view-navigator' },
    { selector: '#Help-tab-label', what: 'вкладка «Довідка»' },
    { selector: '.main-nav.hasnotebookbar #document-header', what: 'шапка документа' },
]

/**
 * Позначка на `<html>` документа редактора: стиль уже застосовано — щоб не
 * додати його вдруге при повторному `Document_Loaded`.
 */
export const HIDDEN_ELEMENTS_MARKER = 'data-wl-collabora-hidden-elements'

export const hiddenElementsCss = (): string =>
    `${COLLABORA_HIDDEN_ELEMENTS.map((element) => element.selector).join(',\n')} {\n    display: none !important;\n}\n`

/**
 * Застосовує стиль у документі редактора.
 *
 * ⚠️ `cool.html` віддається з CSP `style-src 'self'`, тож вставлений
 * `<style>` браузер блокує («Applying inline style violates the following
 * Content Security Policy directive»): елемент є, правил немає, усе видно.
 * Легасі працював зі старшою версією без цього заголовка. CSP не стосується
 * конструйованих таблиць стилів (`new CSSStyleSheet()` +
 * `adoptedStyleSheets`), тож стиль іде саме так. `<style>` лишається запасним
 * шляхом для середовища без `adoptedStyleSheets`.
 *
 * Документ з іншого origin браузер не віддасть (виняток безпеки) — тоді
 * нічого не робимо: редактор працює й так, лише без приховування (це було б
 * порушенням варіанта B, і його видно в акті).
 */
export const injectHiddenElementsStyle = (frame: HTMLIFrameElement | null): boolean => {
    try {
        const win = frame?.contentWindow as (Window & typeof globalThis) | null | undefined
        const doc = win?.document
        const root = doc?.documentElement

        if (!win || !doc || !root) return false
        if (root.hasAttribute(HIDDEN_ELEMENTS_MARKER)) return true

        if (Array.isArray(doc.adoptedStyleSheets) && typeof win.CSSStyleSheet === 'function') {
            // Таблиця — з realm документа `iframe`: чужу браузер не прийме
            const sheet = new win.CSSStyleSheet()
            sheet.replaceSync(hiddenElementsCss())
            doc.adoptedStyleSheets = [...doc.adoptedStyleSheets, sheet]
        } else {
            if (!doc.head) return false

            const style = doc.createElement('style')
            style.textContent = hiddenElementsCss()
            doc.head.appendChild(style)
        }

        root.setAttribute(HIDDEN_ELEMENTS_MARKER, '')

        return true
    } catch {
        return false
    }
}

/** Позначка на `<html>` документа редактора: вікно привітання закрито. */
export const WELCOME_CLOSED_MARKER = 'data-wl-collabora-welcome-closed'

const WELCOME_SELECTOR = '.iframe-welcome-wrap'

/**
 * Закриває вікно привітання Collabora так само, як його хрестик.
 *
 * Одного CSS тут мало. Collabora 25.04 відкриває вікно (`L.iframeDialog`,
 * повідомлення `welcome-show`), коли документ уже завантажено, і при цьому
 * переводить фокус в `iframe` вікна, а `map._iframeDialog` лишається
 * заповненим — `map.editorHasFocus()` повертає `false`, поки вікно не
 * закриють. Схована, але відкрита модалка забирала б курсор і клавіатуру в
 * полотна. Тому, щойно обгортка стає видимою (`style.display` вже не `none`),
 * надсилаємо вікну редактора `welcome-close` — його власне повідомлення
 * закриття: Collabora прибирає вікно, повертає фокус полотну й запам'ятовує
 * версію (`WSDWelcomeVersion`), тож у цьому браузері вікно більше не
 * з'являється. Вимкнути вікно інакше хост не може: параметра URL чи
 * `postMessage` для нього немає, а `welcome.enable` образ CODE не дає.
 */
export const closeWelcomeDialog = (frame: HTMLIFrameElement | null): boolean => {
    try {
        const win = frame?.contentWindow as (Window & typeof globalThis) | null | undefined
        const doc = win?.document

        if (!win || !doc?.body) return false

        // Обгортку вікна Collabora додає в `body` прихованою, а `show()` знімає
        // `display: none`; при невдалому завантаженні вікно перестворюється
        let watchedWrap: HTMLElement | null = null

        const check = (): void => {
            const wrap = doc.querySelector<HTMLElement>(WELCOME_SELECTOR)

            if (!wrap) return

            if (wrap.style.display === 'none') {
                if (wrap !== watchedWrap) {
                    wrapObserver.disconnect()
                    wrapObserver.observe(wrap, { attributes: true, attributeFilter: ['style'] })
                    watchedWrap = wrap
                }

                return
            }

            wrapObserver.disconnect()
            bodyObserver.disconnect()
            win.postMessage(JSON.stringify({ MessageId: 'welcome-close' }), win.location.origin)
            doc.documentElement.setAttribute(WELCOME_CLOSED_MARKER, '')
        }

        const wrapObserver = new win.MutationObserver(check)
        const bodyObserver = new win.MutationObserver(check)

        bodyObserver.observe(doc.body, { childList: true })
        check()

        return true
    } catch {
        return false
    }
}
